// Контракт API пульта (план M, задача M8 = C2-6): уроки после закрытого диалога — экран «Уроки».
//
// - `GET /api/v1/lessons?status=new|accepted|rejected` (admin и editor; viewer — 403) → `{ items, counts, at }`: уроки
//   одного статуса (по умолчанию новые), новые — от свежих, разобранные — от последних разобранных; не больше 200;
//   `counts` — сколько уроков в каждом статусе (вкладки);
// - `POST /api/v1/lessons/:id/accept` и `/reject` (admin и editor) `{}` → `{ undoId }`: статус, кто и когда разобрал,
//   строка журнала `lesson.accept` / `lesson.reject`; уже разобранный — 409 `lesson_reviewed` «Урок уже разобран»;
// - `POST /api/v1/lessons/undo` (admin и editor) `{ undoId }` → `{ ok: true, dialogId }`: отмена своего разбора урока в
//   течение 10 минут (правила — как у отмены разбора диалога, D11). Тост «Отменить» пульт держит 10 с.
//
// Телефоны в текстах урока полностью видит только admin, остальным — маска.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';
import { MomentSchema } from './common.js';

/** Статус урока (копия `LESSON_STATUSES` схемы БД): новый, принят, отклонён. */
export const LESSON_STATUSES = ['new', 'accepted', 'rejected'] as const;
export const LessonStatusSchema = z.enum(LESSON_STATUSES);
export type LessonStatusView = z.infer<typeof LessonStatusSchema>;

/** Статус словами — вкладки экрана. */
export const LESSON_STATUS_RU: Readonly<Record<LessonStatusView, string>> = {
  new: 'Новые',
  accepted: 'Принятые',
  rejected: 'Отклонённые',
};

/** Вид предложения урока (копия `LESSON_PROPOSAL_KINDS` схемы БД). */
export const LESSON_PROPOSAL_KINDS = ['kb_article', 'example', 'reglament', 'none'] as const;
export const LessonProposalKindSchema = z.enum(LESSON_PROPOSAL_KINDS);
export type LessonProposalKindView = z.infer<typeof LessonProposalKindSchema>;

/** Вид предложения словами. */
export const LESSON_PROPOSAL_KIND_RU: Readonly<Record<LessonProposalKindView, string>> = {
  kb_article: 'статья базы',
  example: 'пример',
  reglament: 'правка регламента',
  none: 'без предложения',
};

/** Насколько Анна хуже человека в диалоге (копия `LESSON_SEVERITIES` схемы БД). */
export const LESSON_SEVERITIES = ['none', 'minor', 'major'] as const;
export const LessonSeveritySchema = z.enum(LESSON_SEVERITIES);
export type LessonSeverityView = z.infer<typeof LessonSeveritySchema>;

/** Серьёзность словами. */
export const LESSON_SEVERITY_RU: Readonly<Record<LessonSeverityView, string>> = {
  none: 'не хуже человека',
  minor: 'немного хуже человека',
  major: 'заметно хуже человека',
};

/** Урок в списке. */
export const LessonItemSchema = z.object({
  id: z.string().min(1),
  dialogId: z.string().min(1),
  /** № тикета Омнидеска — ссылка на разбор диалога; не известен — `null`. */
  ticketNumber: z.number().int().nullable(),
  /** Когда урок записан. */
  at: MomentSchema,
  status: LessonStatusSchema,
  /** Что сработало. */
  workedRu: z.string(),
  /** Чего не хватило. */
  missingRu: z.string(),
  proposalKind: LessonProposalKindSchema,
  proposalKindRu: z.string(),
  /** Предложение: тема и ответ, пара «клиент → как ответить» или правило одной строкой. */
  proposal: z.string(),
  severity: LessonSeveritySchema,
  severityRu: z.string(),
  /** Кто разобрал (короткое имя: «Влад Т.»); не разобран или сотрудника нет в пульте — `null`. */
  reviewedByName: z.string().nullable(),
  reviewedAt: MomentSchema.nullable(),
});
export type LessonItem = z.infer<typeof LessonItemSchema>;

export const LessonCountsSchema = z.object({
  new: z.number().int().nonnegative(),
  accepted: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
});
export type LessonCounts = z.infer<typeof LessonCountsSchema>;

export const LessonsResponseSchema = z.object({
  items: z.array(LessonItemSchema),
  counts: LessonCountsSchema,
  /** Момент ответа сервера. */
  at: MomentSchema,
});
export type LessonsResponse = z.infer<typeof LessonsResponseSchema>;

/** Ответ на «Принять» и «Отклонить»: id строки журнала — для «Отменить». */
export const LessonReviewResponseSchema = z.object({ undoId: z.string().min(1) });
export type LessonReviewResponse = z.infer<typeof LessonReviewResponseSchema>;

/** Сколько уроков в одном списке, не больше. */
export const LESSONS_LIST_MAX = 200;

/** Сколько держится тост «Отменить» после разбора урока, мс. */
export const LESSON_UNDO_TOAST_MS = 10_000;

/** Тексты отказов уроков. */
export const LESSON_ERRORS_RU = {
  badStatus: 'status — new (новые), accepted (принятые) или rejected (отклонённые).',
  notFound: 'Урок не найден',
} as const;

/**
 * Коды ошибок уроков и их HTTP-статусы (копия `API_ERROR_STATUS`, `src/console/errors.ts`; совпадение держит
 * `tests/console/lessons.test.ts`).
 */
export const LESSON_ERROR_STATUS = {
  /** Неверный `status` списка, тело не того вида. */
  bad_request: 400,
  /** Нет прав: уроки открыты admin и editor. */
  forbidden: 403,
  /** Урока нет. */
  not_found: 404,
  /** Урок уже разобран. */
  lesson_reviewed: 409,
  undo_not_found: 404,
  undo_not_yours: 403,
  undo_expired: 410,
  undo_conflict: 409,
} as const;
export type LessonErrorCode = keyof typeof LESSON_ERROR_STATUS;

/** Постоянные тексты ошибок уроков (копия `API_ERRORS_RU`). */
export const LESSON_FIXED_ERRORS_RU = {
  lesson_reviewed: 'Урок уже разобран',
  undo_not_found: 'Нечего отменять: действие не найдено',
  undo_not_yours: 'Отменить можно только своё действие',
  undo_conflict: 'Запись уже изменилась — отмена не применена',
} as const;
